import React, { useState, useEffect, useCallback } from 'react';
import { calculateSignaturePageOrder } from './utils/pageCalculations';
import {
  loadPdfLib,
  loadPdfProcessing,
  loadZipPackager,
  prefetchFileLoadDeps,
  prefetchBookletDeps,
} from './utils/lazyPdfDeps';
import { PAPER_SIZES } from './constants';
import { trackPageView, trackDownload } from './utils/analytics';
import usePaperSettings from './hooks/usePaperSettings';
import useSignatureSettings from './hooks/useSignatureSettings';
import usePdfTransformSettings from './hooks/usePdfTransformSettings';
import useMarkupSettings from './hooks/useMarkupSettings';
import './css/base.css';
import './css/layout.css';
import './css/responsive.css';
import './css/components/buttons.css';
import './css/components/diagram.css';
import './css/components/forms.css';
import './css/components/panels.css';
import './css/components/processing.css';
import './css/components/tutorial.css';
import './css/components/version.css';
import './css/components/export-screen.css';
import PageLayoutDiagram from './components/PageLayoutDiagram';
import Tutorial from './components/Tutorial';
import ProcessingPanel from './components/ProcessingPanel';
import ExportScreen from './components/ExportScreen';
import VersionIndicator from './components/VersionIndicator';
import PageLayoutPanel from './components/UI/PageLayoutPanel';
import BookbindingPanel from './components/UI/BookbindingPanel';
import PdfCustomizationPanel from './components/UI/PdfCustomizationPanel';
import DebugPanel from './components/UI/DebugPanel';
import MarkupPanel from './components/UI/MarkupPanel';


function sanitizeFilename(name) {
  return name
    .replace(/\.pdf$/i, '')
    .replace(/[/\\:*?"<>|]/g, '_');
}

/**
 * Main application component that handles PDF processing and user interface
 * @component
 */
function App() {
  // Settings hooks
  const {
    outputPaperSize, setOutputPaperSize,
    customPaperWidth, setCustomPaperWidth,
    customPaperHeight, setCustomPaperHeight,
    useMillimeters, setUseMillimeters,
  } = usePaperSettings();

  const {
    sheetsPerSignature, setSheetsPerSignature,
    fillLastSignature, setFillLastSignature,
    startPadding, setStartPadding,
    endPadding, setEndPadding,
  } = useSignatureSettings();

  const {
    pdfScale, setPdfScale,
    pdfTranslateX, setPdfTranslateX,
    pdfTranslateY, setPdfTranslateY,
    scaleFromSheetCenter, setScaleFromSheetCenter,
  } = usePdfTransformSettings();

  const {
    showHoleGuides, setShowHoleGuides,
    numberOfHoles, setNumberOfHoles,
    showFoldLine, setShowFoldLine,
    showPageNumbers, setShowPageNumbers,
    showPageImages, setShowPageImages,
    showOutputPageNumbers, setShowOutputPageNumbers,
    pageNumberOffset, setPageNumberOffset,
    pageNumberFontSize, setPageNumberFontSize,
  } = useMarkupSettings();

  // Export settings
  const [isDuplex, setIsDuplex] = useState(true);

  // UI and processing state
  const [pdfFile, setPdfFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPackaging, setIsPackaging] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sourcePageCount, setSourcePageCount] = useState(null);
  const [currentSignatures, setCurrentSignatures] = useState(null);
  const [pageOrientation, setPageOrientation] = useState('portrait');
  const [showTutorial, setShowTutorial] = useState(() => {
    return localStorage.getItem('tutorialComplete') !== 'true';
  });
  const [showPostUploadTutorial, setShowPostUploadTutorial] = useState(false);
  const [showExportTutorial, setShowExportTutorial] = useState(false);
  const [processedZip, setProcessedZip] = useState(null);
  const [isExportScreenOpen, setIsExportScreenOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('pdfCustomization');

  const getActivePaperSize = () => {
    if (outputPaperSize === 'CUSTOM') {
      return {
        width: Math.round(customPaperWidth * 72),
        height: Math.round(customPaperHeight * 72),
        name: 'Custom Size',
      };
    }

    return PAPER_SIZES[outputPaperSize] || PAPER_SIZES.LETTER;
  };

  // Pull the PDF chunks in during idle time so the first file drop doesn't
  // have to wait on the network for them.
  useEffect(() => {
    prefetchFileLoadDeps();
  }, []);

  useEffect(() => {
    if (sourcePageCount !== null) {
      prefetchBookletDeps();
    }
  }, [sourcePageCount]);

  useEffect(() => {
    if (sourcePageCount !== null && !localStorage.getItem('postUploadTutorialComplete')) {
      if (localStorage.getItem('tutorialComplete') === 'true') {
        setShowPostUploadTutorial(true);
      }
    }
  }, [sourcePageCount]);

  useEffect(() => {
    if (isExportScreenOpen && !localStorage.getItem('exportTutorialComplete')) {
      setShowExportTutorial(true);
    } else if (!isExportScreenOpen) {
      setShowExportTutorial(false);
    }
  }, [isExportScreenOpen]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };


  //region Load PDF Details
  const loadPdfDetails = async (file) => {
    try {
      const [fileBuffer, { PDFDocument }] = await Promise.all([
        file.arrayBuffer(),
        loadPdfLib(),
      ]);
      const pdfDoc = await PDFDocument.load(fileBuffer, { ignoreEncryption: true });
      
      // Check orientation of all pages
      const orientations = pdfDoc.getPages().map(page => {
        const { width, height } = page.getSize();
        return width > height ? 'landscape' : 'portrait';
      });
      
      // Count occurrences of each orientation
      const orientationCounts = orientations.reduce((acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      }, {});
      
      // Use the most common orientation
      const dominantOrientation = Object.entries(orientationCounts)
        .reduce((a, b) => (a[1] > b[1] ? a : b))[0];
      
      setPageOrientation(dominantOrientation);
      setSourcePageCount(pdfDoc.getPageCount());
    } catch (err) {
      setError('Error reading PDF: ' + err.message);
    }
  };

  const handleFileSelected = async (file) => {
    setSourcePageCount(null);
    setCurrentSignatures(null);
    setPdfFile(null);
    setError(null);

    if (file.type === 'application/pdf') {
      setPdfFile(file);
      await loadPdfDetails(file);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    await handleFileSelected(file);
  };

  //endregion

  //region Process PDF
  const handleProcessPDF = async (file) => {
    try {
      setIsProcessing(true);
      setError(null);
      setProcessedZip(null);

      const baseName = sanitizeFilename(file.name);

      // Normally already resolved via prefetchBookletDeps; awaited together so
      // a failure in either surfaces through the same catch below.
      const [{ processPDF }, { packZip }] = await Promise.all([
        loadPdfProcessing(),
        loadZipPackager(),
      ]);

      const pdfResult = await processPDF(file, {
        startPadding,
        endPadding,
        sheetsPerSignature,
        fillLastSignature,
        showFoldLine,
        showHoleGuides,
        numberOfHoles,
        showOutputPageNumbers,
        pageNumberFontSize,
        pageNumberOffset,
        showPageNumbers,
        scaleFromSheetCenter,
        pdfScale,
        pdfTranslateX,
        pdfTranslateY,
        paperSize: getActivePaperSize(),
        isDuplex,
      });

      setIsProcessing(false);
      setIsPackaging(true);

      const pdfs = isDuplex
        ? [{ blob: pdfResult, filename: `${baseName}-booklet.pdf` }]
        : [
            { blob: pdfResult[0], filename: `${baseName}-odd_pages.pdf` },
            { blob: pdfResult[1], filename: `${baseName}-even_pages.pdf` },
          ];

      const zipBlob = await packZip(pdfs);
      setProcessedZip({ blob: zipBlob, filename: `${baseName}-booklet.zip` });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
      setIsPackaging(false);
    }
  };

  //endregion






  useEffect(() => {
    trackPageView();
  }, []);

  useEffect(() => {
    if (sourcePageCount) {
      // Add padding to the total page count
      const paddedPageCount = sourcePageCount + startPadding + endPadding;
      
      // Calculate signature layout
      const signatures = calculateSignaturePageOrder(
        paddedPageCount, 
        sheetsPerSignature, 
        fillLastSignature
      );
      setCurrentSignatures(signatures);
    }
  }, [sourcePageCount, sheetsPerSignature, fillLastSignature, startPadding, endPadding]);

  const handleTutorialComplete = () => {
    if (showTutorial) {
      setShowTutorial(false);
      localStorage.setItem('tutorialComplete', 'true');
      if (sourcePageCount !== null && !localStorage.getItem('postUploadTutorialComplete')) {
        setShowPostUploadTutorial(true);
      }
    }
    if (showPostUploadTutorial) {
      setShowPostUploadTutorial(false);
      localStorage.setItem('postUploadTutorialComplete', 'true');
    }
    if (showExportTutorial) {
      setShowExportTutorial(false);
      localStorage.setItem('exportTutorialComplete', 'true');
    }
  };

  const handleFileInput = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await handleFileSelected(file);
  };

  const handleDownload = () => {
    if (processedZip) {
      trackDownload();
      const url = URL.createObjectURL(processedZip.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = processedZip.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleClosePanel = () => {
    setProcessedZip(null);
    setIsProcessing(false);
    setIsPackaging(false);
  };

  const handleExport = () => {
    setIsExportScreenOpen(false);
    handleProcessPDF(pdfFile);
  };

  const toggleMenu = (key) => {
    setActiveMenu((current) => (current === key ? null : key));
  };

  const handleTutorialTargetFocus = useCallback((element) => {
    if (!element) {
      return;
    }

    const menuKey = element.getAttribute('data-menu-key');
    if (menuKey) {
      setActiveMenu(menuKey);

      return new Promise((resolve) => {
        // Wait for the menu open/close CSS transition to settle before measuring
        setTimeout(() => {
          const scrollContainer = document.querySelector('.sidebar-scroll');
          if (scrollContainer) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const isAbove = elementRect.top < containerRect.top;
            const isBelow = elementRect.bottom > containerRect.bottom;

            if (isAbove || isBelow) {
              scrollContainer.scrollTo({
                top: element.offsetTop - scrollContainer.clientHeight / 2 + element.clientHeight / 2,
                behavior: 'smooth'
              });
              setTimeout(resolve, 350);
            } else {
              resolve();
            }
          } else if (typeof element.scrollIntoView === 'function') {
            element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            setTimeout(resolve, 350);
          } else {
            resolve();
          }
        }, 300);
      });
    }

    const pageLayoutContainer = element.closest('.page-layout-diagram');
    if (pageLayoutContainer) {
      const isOutputSection = element.matches('.diagram-section:last-child');

      const ensureVisibleWithinDiagram = () => {
        const containerRect = pageLayoutContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const isAbove = elementRect.top < containerRect.top;
        const isBelow = elementRect.bottom > containerRect.bottom;

        if (isAbove || isBelow) {
          const targetOffset = element.offsetTop - pageLayoutContainer.clientHeight / 2 + element.clientHeight / 2;
          pageLayoutContainer.scrollTo({
            top: Math.max(targetOffset, 0),
            behavior: 'smooth'
          });
        }
      };

      if (isOutputSection) {
        return new Promise((resolve) => {
          const cleanup = () => {
            pageLayoutContainer.removeEventListener('scroll', handleScroll);
            clearTimeout(fallbackTimeout);
          };

          const handleScroll = () => {
            const containerRect = pageLayoutContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const fullyVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom + 1;

            if (fullyVisible) {
              cleanup();
              window.requestAnimationFrame(() => resolve());
            }
          };

          const fallbackTimeout = setTimeout(() => {
            cleanup();
            resolve();
          }, 500);

          pageLayoutContainer.addEventListener('scroll', handleScroll, { passive: true });
          pageLayoutContainer.scrollTo({
            top: pageLayoutContainer.scrollHeight,
            behavior: 'smooth'
          });

          window.requestAnimationFrame(handleScroll);
        });
      }

      ensureVisibleWithinDiagram();
      return;
    }

    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [setActiveMenu]);

  const menuItems = [
    {
      key: 'pdfCustomization',
      label: 'PDF Customization',
      description: 'Scale and position your pages',
      className: 'pdf-customization-menu',
      content: (
        <PdfCustomizationPanel
          pdfScale={pdfScale}
          setPdfScale={setPdfScale}
          pdfTranslateX={pdfTranslateX}
          setPdfTranslateX={setPdfTranslateX}
          pdfTranslateY={pdfTranslateY}
          setPdfTranslateY={setPdfTranslateY}
          scaleFromSheetCenter={scaleFromSheetCenter}
          setScaleFromSheetCenter={setScaleFromSheetCenter}
        />
      )
    },
    {
      key: 'pageLayout',
      label: 'Page Layout',
      description: 'Configure signatures and padding',
      className: 'page-layout-menu',
      content: (
        <PageLayoutPanel
          sheetsPerSignature={sheetsPerSignature}
          setSheetsPerSignature={setSheetsPerSignature}
          fillLastSignature={fillLastSignature}
          setFillLastSignature={setFillLastSignature}
          startPadding={startPadding}
          setStartPadding={setStartPadding}
          endPadding={endPadding}
          setEndPadding={setEndPadding}
        />
      )
    },
    {
      key: 'markup',
      label: 'Markup Options',
      description: 'Add page numbers and other markings',
      className: 'markup-menu',
      content: (
        <MarkupPanel
          showOutputPageNumbers={showOutputPageNumbers}
          setShowOutputPageNumbers={setShowOutputPageNumbers}
          pageNumberOffset={pageNumberOffset}
          setPageNumberOffset={setPageNumberOffset}
          pageNumberFontSize={pageNumberFontSize}
          setPageNumberFontSize={setPageNumberFontSize}
        />
      )
    },
    {
      key: 'bookbinding',
      label: 'Bookbinding Guides',
      description: 'Show fold lines and hole placements',
      className: 'bookbinding-menu',
      content: (
        <BookbindingPanel
          showHoleGuides={showHoleGuides}
          setShowHoleGuides={setShowHoleGuides}
          numberOfHoles={numberOfHoles}
          setNumberOfHoles={setNumberOfHoles}
          showFoldLine={showFoldLine}
          setShowFoldLine={setShowFoldLine}
        />
      )
    },
    {
      key: 'debug',
      label: 'Debug Options',
      description: 'Toggle layout debugging tools',
      className: 'debug-menu',
      content: (
        <DebugPanel
          showPageNumbers={showPageNumbers}
          setShowPageNumbers={setShowPageNumbers}
          showPageImages={showPageImages}
          setShowPageImages={setShowPageImages}
        />
      )
    }
  ];

  return (
    <div className="App">
      <VersionIndicator />
      <header className="App-header">
        <h1>EnBooken - PDF to Booklet Converter</h1>
        <p className="definition">
          <span className="word">en·book·en</span> <span className="pronunciation">/enˈbo͝okən/</span> <span className="type">verb</span>
          <br />
          <span className="meaning">to transform any PDF into a properly arranged, printable book format for bookbinding</span>
        </p>
      </header>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="sidebar-scroll">
            <nav className="sidebar-menu">
              {menuItems.map(({ key, label, description, content, className }) => {
                const contentId = `${key}-panel`;
                return (
                  <section
                    key={key}
                    data-menu-key={key}
                    className={`menu-item ${className} ${activeMenu === key ? 'active' : ''}`}
                  >
                    <button
                      type="button"
                      className="menu-toggle"
                      onClick={() => toggleMenu(key)}
                      aria-expanded={activeMenu === key}
                      aria-controls={contentId}
                    >
                      <div className="menu-label">
                        <span className="menu-title">{label}</span>
                        {description && <span className="menu-description">{description}</span>}
                      </div>
                      <span className="menu-indicator" aria-hidden="true" />
                    </button>
                    <div
                      id={contentId}
                      className="menu-content"
                      aria-hidden={activeMenu !== key}
                    >
                      {content}
                    </div>
                  </section>
                );
              })}
            </nav>
          </div>

          <div className="sidebar-footer">
            <button
              className="primary-action"
              onClick={() => pdfFile && setIsExportScreenOpen(true)}
              disabled={!pdfFile || isProcessing}
            >
              Create Booklet
            </button>
          </div>
        </aside>

        <main className="workspace">
          <div
            className={`interactive-diagram diagram-dropzone ${isDragging ? 'drag-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.querySelector('input[type="file"]').click()}
          >
            {pdfFile && (
              <div className="pdf-title">
                {pdfFile.name}
              </div>
            )}
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
            <PageLayoutDiagram
              sourcePages={sourcePageCount}
              signatures={currentSignatures}
              sheetsPerSignature={sheetsPerSignature}
              pageOrientation={pageOrientation}
              showHoleGuides={showHoleGuides}
              showFoldLine={showFoldLine}
              startPadding={startPadding}
              endPadding={endPadding}
              showPageNumbers={showPageNumbers}
              showPageImages={showPageImages}
              pdfFile={pdfFile}
              pdfScale={pdfScale}
              pdfTranslateX={pdfTranslateX}
              pdfTranslateY={pdfTranslateY}
              scaleFromSheetCenter={scaleFromSheetCenter}
              paperSize={getActivePaperSize()}
              showOutputPageNumbers={showOutputPageNumbers}
              pageNumberOffset={pageNumberOffset}
              pageNumberFontSize={pageNumberFontSize}
            />
          </div>

          {error && <div className="error-message">{error}</div>}
        </main>
      </div>

      <footer className="privacy-section">
        <p className="privacy-notice">
          Privacy Notice: This app stores your settings locally in your browser. If you choose to donate,
          payment processing is handled securely by Stripe and no payment information is ever stored by EnBooken.
          Your files never leave your device. We count page visits and downloads; no cookies, no tracking
          across sites, and nothing about you or your files is recorded.
        </p>
      </footer>

      <ExportScreen
        isOpen={isExportScreenOpen}
        onClose={() => setIsExportScreenOpen(false)}
        onExport={handleExport}
        sourcePageCount={sourcePageCount}
        currentSignatures={currentSignatures}
        sheetsPerSignature={sheetsPerSignature}
        outputPaperSize={outputPaperSize}
        setOutputPaperSize={setOutputPaperSize}
        customPaperWidth={customPaperWidth}
        setCustomPaperWidth={setCustomPaperWidth}
        customPaperHeight={customPaperHeight}
        setCustomPaperHeight={setCustomPaperHeight}
        useMillimeters={useMillimeters}
        setUseMillimeters={setUseMillimeters}
        isDuplex={isDuplex}
        setIsDuplex={setIsDuplex}
      />

      <ProcessingPanel
        isProcessing={isProcessing}
        isPackaging={isPackaging}
        zipResult={processedZip}
        onDownload={handleDownload}
        onClose={handleClosePanel}
      />

      {(showTutorial || showPostUploadTutorial || showExportTutorial) && (
        <Tutorial
          onComplete={handleTutorialComplete}
          hasPdfUploaded={sourcePageCount !== null}
          onTargetFocus={handleTutorialTargetFocus}
          isExportOpen={showExportTutorial}
        />
      )}
    </div>
  );
}

export default App;